import { useCallback } from "react";
import type { DragEvent, ReactNode } from "react";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  FolderIcon,
} from "@heroicons/react/24/solid";
import { cn } from "@/lib/utils";
import { FileTypeIcon } from "@/components/icons/file-type-icon";
import { Badge } from "@/components/ui/badge";
import type { CodeEditorDraftEntry } from "@/stores/slices/code-editor.slice";

export type FileTreeNode = {
  name: string;
  path: string;
  type: "file" | "directory";
  children?: Array<FileTreeNode>;
  readOnly?: boolean;
  isDraft?: boolean;
  matchesQuery?: boolean;
  hasMatchingDescendants?: boolean;
};

function shortenFileName(name: string, depth: number, baseMax = 28): string {
  const max = Math.max(10, baseMax - depth * 3);
  if (name.length <= max) return name;

  const dot = name.lastIndexOf(".");
  const ext = dot >= 0 ? name.slice(dot) : "";
  const base = dot >= 0 ? name.slice(0, dot) : name;

  const available = Math.max(5, max - ext.length - 1);

  if (depth >= 2) {
    return `${name.slice(0, Math.max(4, max - 1))}…`;
  }

  let head = Math.min(base.length, Math.max(8, Math.ceil(available * 0.6)));
  let tail = Math.max(3, Math.min(base.length - head, available - head));

  if (head + tail >= base.length) {
    head = Math.min(base.length, max - ext.length - 1);
    tail = Math.max(0, base.length - head);
  }

  const tailText = tail > 0 ? base.slice(-tail) : "";
  return `${base.slice(0, head)}…${tailText}${ext}`;
}

type BuildFileTreeInput = {
  path: string;
  readOnly?: boolean;
  isDraft?: boolean;
};

export function buildFileTree<T extends BuildFileTreeInput>(
  files: Array<T>,
): Array<FileTreeNode> {
  const root: FileTreeNode = {
    name: "",
    path: "",
    type: "directory",
    children: [],
  };

  for (const file of files) {
    const segments = file.path.split("/").filter(Boolean);
    let current: FileTreeNode = root;
    let currentPath = "";

    segments.forEach((segment, index) => {
      currentPath = currentPath ? `${currentPath}/${segment}` : segment;
      const isFile = index === segments.length - 1;

      if (isFile) {
        current.children?.push({
          name: segment,
          path: file.path,
          type: "file",
          readOnly: Boolean(file.readOnly),
          isDraft: Boolean(file.isDraft),
        });
        return;
      }

      let child = current.children?.find(
        (node) => node.name === segment && node.type === "directory",
      );

      if (!child) {
        child = {
          name: segment,
          path: currentPath,
          type: "directory",
          children: [],
        } satisfies FileTreeNode;
        current.children?.push(child);
      }

      current = child;
    });
  }

  const sortNodes = (nodes: Array<FileTreeNode> | undefined) => {
    if (!nodes) return;
    nodes.sort((a, b) => {
      if (a.type === b.type) {
        return a.name.localeCompare(b.name);
      }
      return a.type === "directory" ? -1 : 1;
    });
    nodes.forEach((node) => sortNodes(node.children));
  };

  sortNodes(root.children);
  return root.children ?? [];
}

type FileTreeProps = {
  nodes: Array<FileTreeNode>;
  expandedDirectories: Set<string>;
  onToggleDirectory: (path: string) => void;
  selectedFile: string | null;
  onSelectFile: (path: string) => void;
  drafts: Record<string, CodeEditorDraftEntry>;
  highlightMatches: (text: string) => ReactNode;
  draggedDirectory: string | null;
  onDirectoryDragOver: (event: DragEvent<HTMLDivElement>, path: string) => void;
  onDirectoryDragLeave: (
    event: DragEvent<HTMLDivElement>,
    path: string,
  ) => void;
  onDirectoryDrop: (event: DragEvent<HTMLDivElement>, path: string) => void;
};

export function FileTree({
  nodes,
  expandedDirectories,
  onToggleDirectory,
  selectedFile,
  onSelectFile,
  drafts,
  highlightMatches,
  draggedDirectory,
  onDirectoryDragOver,
  onDirectoryDragLeave,
  onDirectoryDrop,
}: FileTreeProps) {
  const renderNodes = useCallback(
    (currentNodes: Array<FileTreeNode>, depth = 0): Array<ReactNode> => {
      return currentNodes.map((node) => {
        if (node.type === "directory") {
          const isExpanded = expandedDirectories.has(node.path);
          const label = highlightMatches(shortenFileName(node.name, depth));
          const isDragTarget = draggedDirectory === node.path;

          return (
            <div
              key={node.path}
              className={cn("text-xs", depth > 0 && "pl-3")}
              onDragOver={(event) => onDirectoryDragOver(event, node.path)}
              onDragLeave={(event) => onDirectoryDragLeave(event, node.path)}
              onDrop={(event) => onDirectoryDrop(event, node.path)}
            >
              <div
                className={cn(
                  "rounded-md transition-colors overflow-hidden px-1 py-0.5",
                  isDragTarget &&
                    "border border-dashed border-primary/40 bg-muted shadow-sm",
                )}
              >
                <button
                  type="button"
                  className={cn(
                    "flex items-center gap-1 w-full min-w-0 text-left px-2 py-1 rounded-sm transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
                    depth > 0 && "pl-0",
                  )}
                  onClick={() => onToggleDirectory(node.path)}
                  title={node.path}
                >
                  {isExpanded ? (
                    <ChevronDownIcon className="size-3" />
                  ) : (
                    <ChevronRightIcon className="size-3" />
                  )}
                  <FolderIcon
                    aria-hidden
                    className="size-3 shrink-0 text-muted-foreground"
                  />
                  <div className="min-w-0 flex-1">
                    <span className="block overflow-hidden text-ellipsis whitespace-nowrap">
                      {label}
                    </span>
                  </div>
                </button>
                {isExpanded && node.children?.length ? (
                  <div
                    className={cn("pl-3 border-l border-border/50 ml-3 py-1")}
                  >
                    {renderNodes(node.children ?? [], depth + 1)}
                  </div>
                ) : null}
              </div>
            </div>
          );
        }

        const isSelected = selectedFile === node.path;
        const isReadOnly = Boolean(node.readOnly);
        const isDirty = Boolean(drafts[node.path]);

        return (
          <button
            type="button"
            key={node.path}
            onClick={() => onSelectFile(node.path)}
            className={cn(
              "flex items-center gap-2 w-full min-w-0 text-left px-4 py-1 text-xs hover:bg-muted/40 rounded-sm transition-colors",
              isSelected && "bg-muted",
              depth > 0 && "pl-6",
            )}
            title={node.path}
          >
            <FileTypeIcon
              name={node.name}
              className={cn(
                "text-muted-foreground",
                isSelected && "text-foreground",
                isReadOnly && !isSelected && "opacity-60",
              )}
            />
            <div className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap">
              <span
                className={cn(
                  isReadOnly && !isSelected
                    ? "text-muted-foreground"
                    : "text-foreground",
                )}
              >
                {highlightMatches(shortenFileName(node.name, depth))}
              </span>
            </div>
            {node.isDraft ? (
              <Badge
                variant="outline"
                size="compact"
                className="uppercase tracking-[0.15em]"
              >
                New
              </Badge>
            ) : null}
            {isDirty && !isSelected && !node.isDraft ? (
              <span className="text-primary text-xs" aria-hidden>
                •
              </span>
            ) : null}
          </button>
        );
      });
    },
    [
      expandedDirectories,
      onToggleDirectory,
      highlightMatches,
      draggedDirectory,
      onDirectoryDragOver,
      onDirectoryDragLeave,
      onDirectoryDrop,
      selectedFile,
      onSelectFile,
      drafts,
    ],
  );

  return <>{renderNodes(nodes)}</>;
}
